import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserType } from '@tcr-erp/shared';
import type { CustomerLifecycleState } from '@/mocks/customers';
import { customerKeys, type CustomerListQuery } from '@/services/queryKeys';
import {
  customerService,
  type CreateCustomerPayload,
  type CustomerListResponse,
  type UpdateCustomerPayload,
} from '@/services/customer.service';

export function useCustomerList(query: CustomerListQuery, options?: Omit<Parameters<typeof useQuery>[0], 'queryKey' | 'queryFn'>) {
  return useQuery<CustomerListResponse, Error>({
    queryKey: customerKeys.list(query),
    queryFn: () => customerService.list(query),
    placeholderData: (p: CustomerListResponse | undefined) => p,
    retry: 0,
    ...options,
  } as any);
}

/** Nhóm khách hàng ít thay đổi — cache 30 phút, giữ 1 giờ */
const GROUPS_STALE = 30 * 60 * 1000;
const GROUPS_GC    = 60 * 60 * 1000;

export function useCustomerGroups(target_type?: UserType | 'all') {
  return useQuery({
    queryKey: customerKeys.groups(target_type),
    queryFn: () => customerService.listGroups(target_type),
    staleTime: GROUPS_STALE,
    gcTime: GROUPS_GC,
    retry: 0,
  });
}

export function useCustomerGroupsDetail(target_type?: UserType | 'all') {
  return useQuery({
    queryKey: customerKeys.groupsDetail(target_type),
    queryFn: () => customerService.listGroupsDetail(target_type),
    staleTime: GROUPS_STALE,
    gcTime: GROUPS_GC,
    retry: 0,
  });
}

/** Invalidate tất cả variants (all, personal, organization, undefined) */
function invalidateGroupCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: customerKeys.groups() });
  qc.invalidateQueries({ queryKey: customerKeys.groupsDetail() });
}

export function useCreateCustomerGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof customerService.createGroup>[0]) => customerService.createGroup(body),
    onSuccess: () => invalidateGroupCaches(qc),
  });
}

export function useUpdateCustomerGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof customerService.updateGroup>[1] }) =>
      customerService.updateGroup(id, body),
    onSuccess: () => invalidateGroupCaches(qc),
  });
}

export function useDeleteCustomerGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customerService.deleteGroup(id),
    onSuccess: () => invalidateGroupCaches(qc),
  });
}


export function useCustomerDetail(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.detail(id ?? ''),
    queryFn: () => customerService.getById(id!),
    enabled: !!id,
    retry: 0,
  });
}

// TCR-1415 — số dư trả trước (nạp/rút tại quầy). Invalidate cả summary lẫn
// transactions sau mỗi lần nạp/rút thành công, để "Số dư hiện tại" và lịch
// sử giao dịch luôn khớp nhau ngay trên UI mà không cần F5 lại trang.
export function useCustomerCreditSummary(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.creditSummary(id ?? ''),
    queryFn: () => customerService.getCreditSummary(id!),
    enabled: !!id,
    retry: 0,
  });
}

export function useCustomerCreditTransactions(id: string | undefined, page = 1) {
  return useQuery({
    queryKey: customerKeys.creditTransactions(id ?? '', page),
    queryFn: () => customerService.listCreditTransactions(id!, page),
    enabled: !!id,
    retry: 0,
  });
}

function invalidateCreditCaches(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: customerKeys.creditSummary(id) });
  qc.invalidateQueries({ queryKey: [...customerKeys.all, 'credit-transactions', id] });
}

export function useDepositCredit(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: number; method?: string; note?: string }) =>
      customerService.depositCredit(id!, body),
    onSuccess: () => id && invalidateCreditCaches(qc, id),
  });
}

export function useWithdrawCredit(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: number; method?: string; note?: string }) =>
      customerService.withdrawCredit(id!, body),
    onSuccess: () => id && invalidateCreditCaches(qc, id),
  });
}

export function useCustomerPurchases(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.purchases(id ?? ''),
    queryFn: () => customerService.listPurchases(id!),
    enabled: !!id,
    retry: 0,
  });
}

// TCR-1552 — lịch sử thay đổi khách hàng (tab "Lịch sử thay đổi").
export function useCustomerLogs(id: string | undefined, page: number = 1, limit: number = 20) {
  return useQuery({
    queryKey: customerKeys.logs(id ?? '', page),
    queryFn: () => customerService.listLogs(id!, { page, limit }),
    enabled: !!id,
    retry: 0,
  });
}



export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomerPayload) =>
      customerService.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCustomerPayload }) =>
      customerService.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

/**
 * TCR-1473 — đổi danh sách NVKD phụ trách (`assigned_sale_ids`). (final fix
 * wave, Fix 7) — BE đã hỗ trợ đầy đủ field này (xem doc comment ở
 * `customerService.updateAssignedSaleIds`, `customer.service.ts`), gap 400
 * trước đây đã đóng — mutation này giờ ghi thật, không còn reject cứng.
 */
export function useUpdateAssignedSaleIds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saleIds }: { id: string; saleIds: string[] }) =>
      customerService.updateAssignedSaleIds(id, saleIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      customerService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

export function useSetCustomerState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: CustomerLifecycleState }) =>
      customerService.setState(id, state),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

/** Fetcher sổ địa chỉ — dùng chung cho hook và các chỗ cần bản mới nhất ngoài cache. */
export function fetchCustomerAddresses(id: string) {
  return customerService.getAddresses(id).then(r => r.data);
}

export function useCustomerAddresses(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.addresses(id ?? ''),
    queryFn: () => fetchCustomerAddresses(id!),
    enabled: !!id,
  });
}

export function useAddCustomerAddress(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => customerService.addAddress(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.addresses(id) }); },
  });
}

export function usePreviewCustomerAddressImport(id: string) {
  return useMutation({
    mutationFn: (file: File) => customerService.previewAddressImport(id, file),
  });
}

export function useImportCustomerAddresses(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => customerService.importAddresses(id, file),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.addresses(id) }); },
  });
}

export function useUpdateCustomerAddress(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ addressId, body }: { addressId: string; body: Record<string, unknown> }) =>
      customerService.updateAddress(id, addressId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.addresses(id) }); },
  });
}

export function useDeleteCustomerAddress(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => customerService.deleteAddress(id, addressId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.addresses(id) }); },
  });
}

export function useSetDefaultCustomerAddress(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => customerService.setDefaultAddress(id, addressId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.addresses(id) }); },
  });
}

export function useCustomerBanks(id: string | undefined) {
  return useQuery({
    queryKey: customerKeys.banks(id ?? ''),
    queryFn: () => customerService.getBanks(id!).then(r => r.data),
    enabled: !!id,
  });
}

export function useAddCustomerBank(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => customerService.addBank(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.banks(id) }); },
  });
}

export function useUpdateCustomerBank(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, body }: { bankId: string; body: Record<string, unknown> }) =>
      customerService.updateBank(id, bankId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.banks(id) }); },
  });
}

export function useDeleteCustomerBank(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankId: string) => customerService.deleteBank(id, bankId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.banks(id) }); },
  });
}

export function useSetDefaultCustomerBank(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankId: string) => customerService.setDefaultBank(id, bankId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: customerKeys.banks(id) }); },
  });
}
